import { DidomiContext, IsMobile } from 'app/app';
import { gtmEvent } from 'app/modules/gtm/service/gtm.service';
import { initializeHome } from 'app/modules/journey/service/step.service';
import { isEndStep } from 'app/modules/journey/service/store/step.selector';
import { AlertDanger } from 'app/shared/components/alert';
import { IRootState } from 'app/shared/reducers';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { failure, loading, RD, SRD, success } from 'srd';
import DesktopHome from './desktop-home/desktop-home';
import './home.scss';
import MobileHome from './mobile-home/mobile-home';
import { getCustomer, handleLogin } from 'app/modules/account/service/account.service';
import { persistSubscriptionTokenFromUrl } from 'app/shared/util/method-util';

export const Home = () => {
  const location = useLocation();
  const [query] = useState(new URLSearchParams(location.search));
  const error = query?.get('error');
  const code = query?.get('code');
  const didomi = useContext(DidomiContext);
  const isMobile = useContext(IsMobile);

  const { subscriptionNumber, shortOfferCode } = useParams<{ subscriptionNumber: string; shortOfferCode: string }>();
  const dispatch = useDispatch();

  // Fallback regex si le param de route n'est pas resolu, mais que l'URL contient bien un numero de souscription.
  const subscriptionNumberFromPath = /^\/(\d{12})(?:\/.*)?$/.exec(location.pathname)?.[1];
  // Reprise de souscription: on arrive avec un numero de souscription sans code OIDC dans l'URL.
  const isSubscriptionResumeFlow = Boolean((subscriptionNumber || subscriptionNumberFromPath) && !code);

  const appStyleCreatedStore = useSelector((state: IRootState) => state.cms.appStyleCreated);
  const isReadyConfigStore = useSelector((state: IRootState) => state.config.isReady);
  const cdiscountZenUrl = useSelector((state: IRootState) => state.config.cdiscountZenUrl);

  const errorSubscription = useSelector((state: IRootState) => state.subscription.errorMessage);

  const errorOffers = useSelector((state: IRootState) => state.offers.errorMessage);
  const selectedOffer = useSelector((state: IRootState) => state.offers.selectedOffer);
  const _isEndStep = useSelector(isEndStep);
  const offer = useSelector((state: IRootState) => state.config.offer);
  const [homeSRD, setHomeSRD] = React.useState<RD<string, string>>(loading());

  useEffect(() => {
    if (subscriptionNumber) {
      persistSubscriptionTokenFromUrl(subscriptionNumber);
    }
  }, [subscriptionNumber]);

  useEffect(() => {
    if (appStyleCreatedStore && isReadyConfigStore) dispatch(initializeHome(subscriptionNumber, shortOfferCode));
  }, [appStyleCreatedStore, isReadyConfigStore]);

  useEffect(() => {
    if (errorOffers && errorOffers !== '') setHomeSRD(failure(errorOffers));
    if (errorSubscription !== '') setHomeSRD(failure(errorSubscription));
  }, [errorOffers, errorSubscription]);

  useEffect(() => {
    const sku =
      selectedOffer?.code ||
      offer?.defaultOffer ||
      null;

    const hasSkuOrSubscription = Boolean(sku || subscriptionNumber);

    if (didomi && hasSkuOrSubscription) {
      setHomeSRD(success(''));

      if (!_isEndStep) {
        dispatch(gtmEvent('Session ouverte', sku ?? subscriptionNumber, '0'));
      }
    }
  }, [selectedOffer, didomi, offer, subscriptionNumber, _isEndStep]);

  useEffect(() => {
    if (!_isEndStep) {
      // Ne pas relancer un login OIDC dans le flux de reprise /connect/{subscriptionNumber}.
      if (isSubscriptionResumeFlow) {
        return;
      }

      if (!code) {
        dispatch(handleLogin()); // Redirection vers l'url d'authentification
      } else {
        getCustomer(code, dispatch).catch(err => {
          console.error('Erreur lors de la récupération de vos informations, veuillez raffraichir svp', err);
          dispatch(handleLogin()); // Si erreur (code expiré, etc.), redirige
        });
      }
    }
  }, [code, _isEndStep, isSubscriptionResumeFlow, dispatch]);

  useEffect(() => {
    // Si une erreur access_denied dans l'url est détectée et qu’une URL de redirection (cdiscountZenUrl) est disponible
    if (error === "access_denied" && cdiscountZenUrl) {
      window.location.href = cdiscountZenUrl;
    }
  }, [error, cdiscountZenUrl]);


  const view = SRD.match({
    notAsked: () => <div></div>,
    loading: () => <div className="app-loading"><div className="lds-dual-ring"></div></div>,
    failure: (e) => <AlertDanger><span>{"Une erreur s'est produite"}</span>{e && <span><br />{e}</span>}</AlertDanger>,
    success: (d) =>
      <div>
        {isMobile ? <MobileHome /> : <DesktopHome />}
      </div>
  }, homeSRD)

  return view;
};

export default Home;
